import { useState, ChangeEvent } from 'react';
import { Box, Button, Typography, TextField } from '@mui/material/';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { Modal, useNotification } from '../..';
import { IModal } from '../Modal';
import { useNavigate } from 'react-router';
import { useSalonStore } from '../../../store';
import { useTranslation } from 'react-i18next';
import { SendCompletedPlanData } from '../../../types';
import { StyledNote } from './ResendPlanModal.styles';

interface IResendPlanModal extends IModal {
  rowId: string;
}

export const ResendPlanModal = ({ open, handleClose, rowId }: IResendPlanModal) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sendCompletedPlan, resetStore } = useSalonStore();

  const [step, setStep] = useState<number>(1);
  const [email, setEmail] = useState('');
  const [email2, setEmail2] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailError2, setEmailError2] = useState(false);

  const { showMessage } = useNotification();

  const payload: SendCompletedPlanData = {
    recipientEmail: email2.length > 0 ? [email, email2] : [email],
  };

  const handleCloseBtn = () => {
    if (step === 3) {
      closeSendForm();
    } else {
      setStep(1);
      handleClose();
    }
  };

  const closeSendForm = () => {
    setStep(1);
    handleClose();
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.trim());
    if (e.target.validity.valid) {
      setEmailError(false);
    } else {
      setEmailError(true);
    }
  };

  const handleEmailChange2 = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail2(e.target.value.trim());
    if (e.target.validity.valid) {
      setEmailError2(false);
    } else {
      setEmailError2(true);
    }
  };

  const handleSendBtn = async () => {
    try {
      const result = await sendCompletedPlan(payload, rowId, showMessage, navigate);
      if (result) {
        resetStore();
        setStep(3);
      } else {
        showMessage(t('sendFormModal.errorSendingPlan'), 'error');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('sendFormModal.errorSendingPlan');
      showMessage(errorMessage, 'error');
    }
  };

  const header = () => (
    <>
      <Typography variant="h6" textTransform="capitalize">
        {step !== 3 && t('sendFormModal.sendReport')}
      </Typography>
      <Typography variant="body2">
        {step === 1 && t('sendFormModal.reportIndicate')}
        {step === 2 && t('sendFormModal.emailIndicate')}
      </Typography>
    </>
  );

  const content = () => {
    if (step === 1) {
      return (
        <>
          <TextField
            id="email-input"
            label={t('sendFormModal.emailAddress')}
            variant="outlined"
            fullWidth
            value={email}
            inputProps={{
              type: 'email',
            }}
            onChange={handleEmailChange}
            error={emailError}
            helperText={emailError ? t('sendFormModal.emailError') : ''}
          />
          <TextField
            sx={{ mt: 1 }}
            id="email-input"
            label={t('sendFormModal.emailAddress')}
            variant="outlined"
            fullWidth
            value={email2}
            inputProps={{
              type: 'email',
            }}
            onChange={handleEmailChange2}
            error={emailError2}
            helperText={emailError2 ? t('sendFormModal.emailError') : ''}
          />
          <StyledNote>{t('sendFormModal.emailNote')}</StyledNote>
        </>
      );
    }

    if (step === 3) {
      return (
        <>
          <Box display="flex" justifyContent="center" sx={{ mb: 1 }}>
            <FactCheckIcon color="secondary" sx={{ fontSize: '64px' }} />
          </Box>
          <Typography variant="body2" textAlign="center">
            {`${t('sendFormModal.planSent')} ${email}!`}
          </Typography>
          {email2.length > 0 && (
            <Typography variant="body2" textAlign="center">
              {`${t('sendFormModal.planSent')} ${email2}!`}
            </Typography>
          )}
        </>
      );
    }
  };

  const buttons = () => {
    if (step === 1) {
      return (
        <>
          <Button
            variant="contained"
            startIcon={<SendRoundedIcon />}
            onClick={handleSendBtn}
            disabled={emailError || emailError2 || email.length === 0}
          >
            Send
          </Button>
        </>
      );
    }

    if (step === 3) {
      return (
        <Button variant="contained" onClick={closeSendForm}>
          {t('close')}
        </Button>
      );
    }
  };

  return (
    <Modal
      open={open}
      handleClose={() => handleCloseBtn()}
      header={header()}
      mainContent={content()}
      buttons={buttons()}
      fullWidth
    />
  );
};
